import { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
  CircularProgress,
  Alert,
  Link,
  useTheme
} from '@mui/material';
import { tokens } from "../../theme";
import authService from '../../services/AuthService';
import keychainLogo from '../../assets/logo_keychain.svg';

const LoginModal = ({ open, onClose }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [username, setUsername] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hasKeychain, setHasKeychain] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [pendingLogin, setPendingLogin] = useState(null);

  // Check if Hive Keychain is available and if on mobile
  useEffect(() => {
    const unsubscribe = authService.subscribe((data) => {
      setHasKeychain(data.hasKeychain);
      setIsMobile(data.isMobile);
      setPendingLogin(data.pendingLogin);
    });

    return () => unsubscribe();
  }, []);

  // Login function with Hive Keychain
  const handleKeychainLogin = async () => {
    if (!username.trim()) {
      setError('Please enter a username');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await authService.login(username.trim());
      // Successfully logged in, close modal
      onClose();
    } catch (err) {
      console.error('Login error:', err);
      setError(err.message || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  // Handle Enter key press for form submission
  const handleKeyPress = (event) => {
    if (event.key === 'Enter' && !loading) {
      handleKeychainLogin();
    }
  };

  // Download link for Hive Keychain
  const getKeychainLink = () => {
    if (isMobile) {
      // Mobile app links
      const isAndroid = /android/i.test(navigator.userAgent);
      if (isAndroid) {
        return 'https://play.google.com/store/apps/details?id=com.mobilekeychain';
      }
      return 'https://apps.apple.com/us/app/hive-keychain/id1552190010';
    } else {
      // Browser extension links
      const isFirefox = navigator.userAgent.toLowerCase().indexOf('firefox') > -1;
      if (isFirefox) {
        return 'https://addons.mozilla.org/en-US/firefox/addon/hive-keychain/';
      }
      return 'https://chrome.google.com/webstore/detail/hive-keychain/jcacnejopjdphbnjgfaaobbfafkihpep';
    }
  };

  return (
    <Dialog open={open} onClose={!loading ? onClose : undefined} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ color: colors.grey[100] }}>
        Sign in with Hive Keychain
      </DialogTitle>
      <DialogContent sx={{ pt: 2 }}>
        {!hasKeychain && !isMobile ? (
          // Desktop browser without Keychain extension
          <Box>
            <Alert severity="warning" sx={{ mb: 2 }}>
              Hive Keychain browser extension not detected. Please install it to continue.
            </Alert>
            <Box display="flex" justifyContent="center" mb={2}>
              <Button
                variant="contained"
                color="secondary"
                component={Link}
                href={getKeychainLink()}
                target="_blank"
                rel="noopener noreferrer"
              >
                Download Hive Keychain Extension
              </Button>
            </Box>
          </Box>
        ) : isMobile && pendingLogin ? (
          // Show waiting screen when mobile authentication is pending
          <Box textAlign="center">
            <CircularProgress size={60} sx={{ my: 2 }} />
            <Typography variant="body1" color={colors.grey[100]} sx={{ mb: 2 }}>
              Please complete the authentication in your Hive Keychain mobile app.
            </Typography>
            <Typography variant="body2" color={colors.grey[400]} sx={{ mb: 2 }}>
              If the app doesn't open automatically, please open it manually and approve the login request.
            </Typography>
          </Box>
        ) : isMobile && !hasKeychain ? (
          // Mobile device without Keychain app
          <Box>
            <Alert severity="warning" sx={{ mb: 2 }}>
              Hive Keychain mobile app not detected. Please install it to continue.
            </Alert>
            <Box display="flex" justifyContent="center" mb={2}>
              <Button
                variant="contained"
                color="secondary"
                component={Link}
                href={getKeychainLink()}
                target="_blank"
                rel="noopener noreferrer"
              >
                Download Hive Keychain App
              </Button>
            </Box>
          </Box>
        ) : (
          // Normal login form for Keychain
          <>
            <Typography variant="body1" color={colors.grey[100]} sx={{ mb: 2 }}>
              Enter your Hive username to sign in with {isMobile ? "Hive Keychain mobile app" : "Hive Keychain"}.
            </Typography>
            
            <TextField
              autoFocus
              margin="dense"
              id="username"
              label="Hive Username"
              type="text"
              fullWidth
              variant="outlined"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              disabled={loading}
              onKeyPress={handleKeyPress}
              sx={{
                input: { color: colors.grey[100] },
                '& .MuiOutlinedInput-root': {
                  '& fieldset': {
                    borderColor: colors.grey[400],
                  },
                  '&:hover fieldset': {
                    borderColor: colors.grey[300],
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: colors.greenAccent[400],
                  },
                },
                '& .MuiInputLabel-root': {
                  color: colors.grey[300],
                },
              }}
            />
          </>
        )}
        
        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </DialogContent>
      <DialogActions sx={{ pb: 3, px: 3 }}>
        <Button onClick={onClose} color="inherit" disabled={loading}>
          Cancel
        </Button>
        <Button 
          onClick={handleKeychainLogin} 
          color="secondary" 
          variant="contained"
          disabled={loading || ((!hasKeychain && !isMobile) || (isMobile && pendingLogin))}
          sx={{ minWidth: 120 }}
        >
          {loading ? (
            <CircularProgress size={24} color="inherit" />
          ) : (
            <>
              <img 
                src={keychainLogo} 
                alt="Hive Keychain" 
                style={{ height: '20px', marginRight: '8px' }} 
              />
              Sign In with Keychain
            </>
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default LoginModal;